// The material table (§31 M1, the design doc's "Materials").
//
// Four lines. Two **ladders**, refined a rung at a time: metal (iron → steel →
// crucible steel) and stone (cobble → stone → marble). Two **resources**,
// never refined, each grade its own thing: special (electrum, diamond) and
// fuel (coal, nuclear). Stone builds structures, metal builds troops, fuel
// pays for raising metal (`DESIGN.metalNeedsFuel`). Critical Spark and
// Trophy are items (phase A), not material.
//
// A **grade** is a rung *within its line*.

import { type PieceType } from "./board";
import { FORGE_BILLS, type Grade, RULES, pieceVariant } from "./rules";

// The lines are `src/shared/industry.ts`, re-exported here.
export { METAL, STONE, SPECIAL, FUEL, type MaterialLine } from "src/shared/industry";
import {
  FUEL,
  METAL,
  STONE,
  SPECIAL,
  type MaterialLine,
  type ResourceId
} from "src/shared/industry";
export const LINE_COUNT = 4;
export const LINE_NAMES = ["Metal", "Stone", "Special", "Fuel"] as const;

const METAL_IDS: readonly ResourceId[] = ["iron", "steel", "crucible-steel"];
const STONE_IDS: readonly ResourceId[] = ["cobble", "stone", "marble"];
const SPECIAL_IDS: readonly ResourceId[] = ["electrum", "diamond"];
const FUEL_IDS: readonly ResourceId[] = ["coal", "nuclear"];

// The designer's words, verbatim. "Gold" leaves the game.
export const MATERIAL_WORDS: Record<ResourceId, string> = {
  iron: "Iron",
  steel: "Steel",
  "crucible-steel": "Crucible Steel",
  cobble: "Cobble",
  stone: "Stone",
  marble: "Marble",
  electrum: "Electrum",
  diamond: "Diamond",
  coal: "Coal",
  nuclear: "Nuclear Fuel"
};

/** The ladders as stable ids, indexed by line. */
export const LADDER_IDS: readonly (readonly ResourceId[])[] = [
  METAL_IDS,
  STONE_IDS,
  SPECIAL_IDS,
  FUEL_IDS
];

/** The same ladders in the designer's words, derived from the ids. */
export const LADDERS: readonly (readonly string[])[] = LADDER_IDS.map((ids) =>
  ids.map((id) => MATERIAL_WORDS[id])
);

/** The special line's members, as grades within it. */
export const ELECTRUM = 0;
export const DIAMOND = 1;

/** The fuel line's members. Coal is mined; nuclear is made (S6) and never dug. */
export const COAL = 0;
export const NUCLEAR = 1;

/**
 * Is this line a **ladder** — refined a rung at a time, a higher rung paying
 * for a lower? Metal and stone. Special and fuel are resources: grade there is
 * identity, so a diamond never pays an electrum row and nothing cooks them.
 */
export const isLadder = (line: number): boolean => line === METAL || line === STONE;

/**
 * Why there cannot be a deposit of `(line, grade)`, or null.
 *
 * **Refused, never defaulted** — the `normalizeRuleset` rule. A deposit rides
 * in the opening command and in `next-stage`, so a rung no ladder has is a
 * client from another build, and putting iron down instead would be two
 * clients replaying one log onto two different boards.
 */
export function depositFault(line: number, grade: number, tier: number = PURE): string | null {
  if (line !== METAL && line !== STONE && line !== SPECIAL && line !== FUEL) {
    return `unknown line: ${line}`;
  }
  if (!Number.isInteger(grade) || grade < 0 || grade > lineTop(line)) {
    return `no ${LINE_NAMES[line]} at grade ${grade}`;
  }
  // Nuclear fuel is built, never dug (the design doc's Deposits).
  if (line === FUEL && grade === NUCLEAR) return "nuclear fuel is not mined";
  if (!Number.isInteger(tier) || tier < PURE || tier >= DEPOSIT_TIERS.length) {
    return `unknown tier: ${tier}`;
  }
  // A quarry is cobble only; the other material of a mixed tile is never cobble.
  if (tier !== PURE && line === STONE) return "a quarry has no tier";
  return null;
}

/**
 * **Deposit tiers** (§31 D1, the design doc's Deposits). A mixed tile is
 * cobble plus one other material; the tier is the other's share. `PURE` is
 * one material only — a quarry, or an authored/older tile.
 */
export const PURE = 0;
export const THIN = 1;
// V2 swapped the words (§33 M1): concentrated is 75%, rich 90%. The ids keep
// their shares, so a log or a hash means what it meant.
export const CONCENTRATED = 2;
export const RICH = 3;

/** Per tier: its word and the other material's share, in percent. */
export const DEPOSIT_TIERS: readonly { word: string; other: number }[] = [
  { word: "", other: 100 },
  { word: "thin", other: 25 },
  { word: "concentrated", other: 75 },
  { word: "rich", other: 90 }
];

/**
 * Cobble units in a fresh mixed tile of `amount`: the tier's cobble share,
 * rounded, leaving at least one of the other.
 */
export function cobbleShare(amount: number, tier: number): number {
  if (tier === PURE || amount <= 1) return 0;
  const share = Math.round((amount * (100 - DEPOSIT_TIERS[tier].other)) / 100);
  return Math.max(0, Math.min(amount - 1, share));
}

/** "rich iron deposit", "stone quarry", "coal deposit". */
export function depositName(line: MaterialLine, grade: number, tier: number): string {
  if (line === STONE) return "stone quarry";
  const what = materialName(line, grade).toLowerCase();
  const word = DEPOSIT_TIERS[tier]?.word ?? "";
  return word ? `${word} ${what} deposit` : `${what} deposit`;
}

/** Top rung of a line. */
export const lineTop = (line: MaterialLine): number => LADDERS[line].length - 1;

/** Top of the metal line — what "fully refined" means: crucible steel. */
export const topMetal = (): number => lineTop(METAL);

/** What a unit of (line, grade) is called. */
export const materialName = (line: MaterialLine, grade: number): string =>
  LADDERS[line]?.[grade] ?? "Unknown";

/** The stable id of a unit of (line, grade). A rung the ladder lacks is a programming error. */
export function materialId(line: MaterialLine, grade: number): ResourceId {
  const id = LADDER_IDS[line]?.[grade];
  if (id === undefined) throw new Error(`no material at line ${line} grade ${grade}`);
  return id;
}

/**
 * May a refinery ever raise this line? The ladders only — grade on special and
 * fuel is identity, not progress. Same ovens, either ladder (§26.1 (4)).
 */
export const refinableLine = isLadder;

/**
 * Does raising a unit of this line one rung cost a fuel point (§31 M2)?
 * Metal, while `DESIGN.metalNeedsFuel`; stone is always free.
 */
export const needsFuel = (line: number, on: boolean): boolean => on && line === METAL;

/** Diamond — the rocket's fuel (§26 F2, decided), a special. */
export const DIAMOND_AT: { line: MaterialLine; grade: number } = { line: SPECIAL, grade: DIAMOND };

/** Electrum — the queen's extra, a special. */
export const ELECTRUM_AT: { line: MaterialLine; grade: number } = { line: SPECIAL, grade: ELECTRUM };

/** Coal — what a refiner burns for a fuel point (§31 M2). */
export const COAL_AT: { line: MaterialLine; grade: number } = { line: FUEL, grade: COAL };

// --- bills -----------------------------------------------------------------

/**
 * One row of a recipe: so many units of this line at this rung — or above, on
 * a ladder (`unitMeets`).
 */
export interface BillRow {
  line: MaterialLine;
  grade: number;
  count: number;
}

/**
 * A recipe is a bill: a list of rows that never share a unit — at most one
 * row per ladder, and resource rows at distinct grades — so rows are paid
 * independently with nothing counted twice.
 */
export type Bill = readonly BillRow[];

/**
 * Which lines have a **source**. All four do: metal comes out of a capture
 * and out of an iron deposit, stone, the specials and coal out of a deposit
 * (§26 F3, §31 M2). The mechanism stays because it is what a row charged in a material
 * nobody can obtain needs — `billFor` drops such a row rather than making the
 * recipe unbuildable — and because the `sourced` parameter is how the bill
 * machinery is exercised either way.
 */
export const LINE_SOURCED: readonly boolean[] = [true, true, true, true];

/**
 * The full authored bill for a piece — `FORGE_BILLS`, the design doc's table
 * (§31 M4). A fresh copy, so a caller may not edit the table through it.
 */
export function mintBill(type: PieceType): Bill {
  return FORGE_BILLS[type].map((row) => ({ ...row }));
}

/**
 * The bill as it can actually be paid **today**: rows whose line has no
 * source yet are waived rather than left unpayable. `sourced` is a parameter
 * so a test can exercise a two-line bill before F3 flips the switch.
 */
export function billFor(type: PieceType, sourced: readonly boolean[] = LINE_SOURCED): Bill {
  return mintBill(type).filter((row) => sourced[row.line]);
}

/**
 * The grade that gates a recipe's tier — the metal row's rung (every bill
 * leads with one): pawn iron, knight/bishop/rook steel, queen and king crucible.
 */
export function mintGradeOf(type: PieceType): Grade {
  return FORGE_BILLS[type][0].grade as Grade;
}

/**
 * The bill for a variant row (§31 P3) — the design's table on the row. A
 * fresh copy, so a caller may not edit the table through it.
 */
export function variantBill(id: number): Bill {
  return pieceVariant(id).bill.map((row) => ({ ...row }));
}

/**
 * What a unit of (line, rung) is worth, cross-line — **one price table**
 * (`RULES.materialValue`), because every "carry the dearest, spend the
 * cheapest" rule needs one answer to "marble vs steel". The reducer routes by
 * it, so it is a rule, not a weight; `w.grade`/`w.gradeStone`/`w.gradeSpecial`
 * are the tuner's per-line copies.
 */
export const materialValue = (line: number, grade: number): number =>
  RULES.materialValue[line]?.[grade] ?? 0;

/**
 * The **value yardstick** a level's `grade` names (deliver, collect, the
 * rocket pad): rung n is worth at least `valueRung(n)`. Metal's three rungs
 * then electrum and diamond — 1, 3, 6, 12, 24 — which is what metal grades
 * 3–4 used to mean before the ladder was tidied to three (§31 M1).
 */
export const valueRung = (grade: number): number => {
  const rungs = [...RULES.materialValue[METAL], ...RULES.materialValue[SPECIAL]];
  return rungs[Math.max(0, Math.min(rungs.length - 1, grade))];
};
